import { Cable } from "../../model/viewModel/cable";
import { ConnectionPoint } from "../../model/viewModel/connectionPoint";
import { Transformer } from "../../model/viewModel/transformer";

import { GroupedConnectionPoint } from "../../model/viewModel/groupedConnectionPoint";
import LinkBoxConnectivityConfigurator from "../LinkBoxConnectivityConfigurator";
import TooltipLayout from "../leaflet-tooltip/TooltipLayout";
import { PointOfConnection } from "../../model/viewModel/pointOfConnection";
import getConsumptionTypeLabel from "../../utils/getConsumptionTypeLabel";
import { Motor } from "../../model/viewModel/motor";
import { Welder } from "../../model/viewModel/welder";

export type Lct = "generator" | "evChargePoint" | "evcharger" | "heatPump";

const renderLctTooltip = (asset: ConnectionPoint, label: string, classNames: string[]) => (
  <div className="d-flex flex-column gap-2">
    {asset.groupedConnectionPoints.map((gcp: GroupedConnectionPoint) => {
      if (!gcp.subGroupConnectionPoints || gcp.subGroupConnectionPoints.length === 0) return null;
      return (
        <div key={gcp.id}>
          <TooltipLayout
            title={label}
            items={gcp.subGroupConnectionPoints
              .filter((sgcp) => sgcp && sgcp.styles && classNames.includes(sgcp.styles.class ?? ""))
              .map((sgcp) => [
                {
                  label: "Phase",
                  text: sgcp.numberOfPhases,
                },
                {
                  label: "Load Profile",
                  text: sgcp.consumerType,
                },
                {
                  label: "Size (kW)",
                  text: `${sgcp.consumptionValue1} (Day), ${sgcp.consumptionValue2} (Night)`,
                },
              ])
              .flat()}
          />
        </div>
      );
    })}
  </div>
);

const renderTransformerTooltip = (transformer: Transformer) => (
  <TooltipLayout
    title="Transformer"
    items={[
      { label: "Rating (kVA)", text: String(transformer.nameplateRating) },
      { label: "Phase", text: String(transformer.numberOfPhases) },
    ]}
  />
);

const renderMotorTooltip = (motor: Motor) => (
  <TooltipLayout
    title="Motor"
    items={[
      { label: `Rating (${motor.ratingUnit})`, text: String(motor.rating) },
      {
        label: "Phase",
        text: String(motor.numberOfPhases),
      },
      {
        label: `Number of Starts (${motor.numberOfStartsUnit})`,
        text: String(motor.numberOfStarts),
      },
    ]}
  />
);

const renderWelderTooltip = (welder: Welder) => (
  <TooltipLayout
    title="Welder"
    items={[
      { label: `Rating (${welder.ratingUnit})`, text: String(welder.rating) },
      {
        label: "Phase",
        text: welder.numberOfPhases,
      },
      {
        label: `Number of Bursts (${welder.numberOfBurstsUnit})`,
        text: String(welder.numberOfBursts),
      },
    ]}
  />
);

const renderPointOfConnectionTooltip = (pointOfConnection: PointOfConnection) => (
  <TooltipLayout
    title="Point of Connection"
    items={[
      { label: "Voltage Drop (%)", text: String(pointOfConnection.voltDropPercent) },
      { label: "Loop Resistance (mΩ)", text: String(pointOfConnection.loopResistanceMilliohm) },
    ]}
  />
);

const renderConnectionPointTooltip = (asset: ConnectionPoint) => {
  const isLct =
    asset?.styles?.class && ["generator", "evChargePoint", "heatPump"].includes(asset.styles.class);
  return (
    <>
      {asset.groupedConnectionPoints.map((gcp: GroupedConnectionPoint, index: number) => (
        <div key={gcp.id}>
          {index > 0 && <div style={{ height: 16 }} />}
          <TooltipLayout
            key={gcp.id}
            title={`${gcp.count > 1 ? `${gcp.count} x ` : ""} ${gcp.styles?.name}`}
            items={[
              { label: "Load Profile", text: gcp.consumerType },
              { label: "Phase", text: gcp.numberOfPhases },
              isLct
                ? null
                : { label: "Demand Type", text: getConsumptionTypeLabel(gcp.consumptionType) },
              isLct
                ? {
                    label: "Size (kW)",
                    text: `${gcp.consumptionValue1} (Day), ${gcp.consumptionValue2} (Night)`,
                  }
                : null,
              ...gcp.subGroupConnectionPoints
                .map((sgcp) => [
                  {
                    label: `${sgcp.styles.name} (kW)`,
                    text: `${sgcp.consumptionValue1} (Day), ${sgcp.consumptionValue2} (Night)`,
                  },
                ])
                .flat(),
            ].filter((item): item is { label: any; text: string } => item !== null)}
          />
        </div>
      ))}
    </>
  );
};

const renderCableTooltip = (asset: Cable) => {
  const items = [
    { label: "Cable Type", text: asset.cableType },
    { label: "Phase", text: asset.numberOfPhases },
  ];

  return (
    <TooltipLayout title={asset.styles.name} items={items}>
      {asset.groupedConnectionPoints?.length > 0 && (
        <div className="p2">
          <h6 className="mb-1 mt-3 px-2 py-0 text-white fw-bold">Distrubuted Consumers</h6>
          <table className="network-element-tooltip-internal-table ml-2 mt-2">
            <tbody>
              <tr>
                <td>Connection</td>
                <td>Profile</td>
                <td>Demand (kWh)</td>
              </tr>
              {asset.groupedConnectionPoints.map((groupedConnectionPoint, index) => (
                <tr key={groupedConnectionPoint.id}>
                  <td>{groupedConnectionPoint.styles.name}</td>
                  <td>
                    {groupedConnectionPoint.count} x {groupedConnectionPoint.consumerType}
                  </td>
                  <td>
                    {`${groupedConnectionPoint.consumptionValue1} (Day) / ${groupedConnectionPoint.consumptionValue2} (Night)`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </TooltipLayout>
  );
};

const renderLinkBoxTooltip = (asset: ConnectionPoint) => {
  if (!asset?.linkBox) {
    return null;
  }
  const linkBoxItems =
    asset.linkBox.numberOfWays === "2-Way"
      ? [
          {
            label: "Connectivity",
            text: asset.linkBox.twoWayConnectivityIsClosed ? "Closed" : "Open",
          },
        ]
      : [];

  return (
    <TooltipLayout title="Link Box" items={linkBoxItems}>
      {asset.linkBox.numberOfWays !== "2-Way" && (
        <div>
          <LinkBoxConnectivityConfigurator
            clickedAsset={asset}
            linkBox={asset.linkBox}
            updateNode={() => {}}
            disabled={true}
          />
        </div>
      )}
    </TooltipLayout>
  );
};

interface NetworkElementTooltipContentProps {
  asset:
    | Cable
    | ConnectionPoint
    | PointOfConnection
    | Transformer
    | Welder
    | Motor
    | PointOfConnection;
  lctType?: Lct;
}

export const NetworkElementTooltipContent = ({
  asset,
  lctType,
}: NetworkElementTooltipContentProps) => {
  if (
    lctType &&
    (asset.styles.type === "connectionPoint" || asset.styles.type === "groupedConnection")
  ) {
    switch (lctType) {
      case "generator":
        return renderLctTooltip(asset as ConnectionPoint, "Generator", ["generator"]);
      case "evChargePoint":
        return renderLctTooltip(asset as ConnectionPoint, "EV Charger", ["evChargePoint"]);
      case "heatPump":
        return renderLctTooltip(asset as ConnectionPoint, "Heat Pump", ["heatPump"]);
      default:
        break;
    }
  }

  if (asset.linkBox !== undefined) {
    return renderLinkBoxTooltip(asset as ConnectionPoint);
  }

  switch (asset.styles.class) {
    case "welder":
      return renderWelderTooltip(asset as Welder);
    case "motor":
      return renderMotorTooltip(asset as Motor);
    case "point-of-connection":
      return renderPointOfConnectionTooltip(asset as PointOfConnection);
  }

  switch (asset.styles.type) {
    case "transformer":
      return renderTransformerTooltip(asset as Transformer);
    case "groupedConnection":
    case "connectionPoint":
      return renderConnectionPointTooltip(asset as ConnectionPoint);
    case "cable":
      return renderCableTooltip(asset as Cable);
    default:
      break;
  }

  return <span>No details</span>;
};
